import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MiniMarkdown, EmptyState, Spinner } from '@/components/common';
import { toast } from 'sonner';
import { BookOpen, Plus, Pencil, Trash2 } from 'lucide-react';

export default function SOPs() {
  const [sops, setSops] = useState(null);
  const [category, setCategory] = useState('all');
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: '', category: 'General', content: '' });
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get('/sops');
    setSops(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const categories = ['all', ...new Set((sops || []).map((s) => s.category))];
  const filtered = (sops || []).filter((s) => category === 'all' || s.category === category);

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error('Title and content required'); return; }
    setBusy(true);
    try {
      if (editing && viewing) {
        const { data } = await api.put(`/sops/${viewing.id}`, form);
        setViewing(data);
        setEditing(false);
        toast.success('SOP updated');
      } else {
        await api.post('/sops', form);
        setCreateOpen(false);
        toast.success('SOP created');
      }
      load();
    } finally { setBusy(false); }
  };

  const del = async (s) => {
    await api.delete(`/sops/${s.id}`);
    setViewing(null);
    toast.success('SOP deleted');
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">SOP Library</h1>
          <div className="page-accent-bar" />
          <p className="mt-2 text-sm text-muted-foreground">Proven playbooks: research, suppliers, store, creatives, launch, optimization, kill/scale rules.</p>
        </div>
        <Button data-testid="sops-add-button" onClick={() => { setForm({ title: '', category: 'General', content: '' }); setCreateOpen(true); }} className="accent-btn gap-2">
          <Plus className="h-4 w-4" /> New SOP
        </Button>
      </div>

      <Tabs value={category} onValueChange={setCategory}>
        <TabsList className="flex h-auto flex-wrap justify-start">
          {categories.map((c) => <TabsTrigger key={c} data-testid={`sops-filter-${c}`} value={c} className="capitalize">{c}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      {sops === null ? (
        <div className="flex justify-center py-10"><Spinner className="h-6 w-6" /></div>
      ) : filtered.length === 0 ? (
        <EmptyState testId="sops-empty" icon={BookOpen} title="No SOPs in this category" subtitle="Create your own playbook so future-you executes without thinking." />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((s) => (
            <Card key={s.id} data-testid="sop-card" className="cursor-pointer transition-colors duration-150 hover:border-primary/40" onClick={() => { setViewing(s); setEditing(false); }}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-primary" />
                  <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] uppercase tracking-wide">{s.category}</span>
                </div>
                <p className="mt-2 text-sm font-semibold">{s.title}</p>
                <p className="mt-1 line-clamp-3 text-xs text-muted-foreground">{s.content.replace(/[#*]/g, '').slice(0, 150)}...</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* View/Edit dialog */}
      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          {viewing && !editing && (
            <>
              <DialogHeader>
                <DialogTitle className="pr-8">{viewing.title}</DialogTitle>
              </DialogHeader>
              <div className="flex gap-2">
                <Button data-testid="sop-edit-button" variant="outline" size="sm" className="gap-1.5" onClick={() => { setForm({ title: viewing.title, category: viewing.category, content: viewing.content }); setEditing(true); }}>
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Button>
                <Button data-testid="sop-delete-button" variant="outline" size="sm" className="gap-1.5 text-rose-500" onClick={() => del(viewing)}>
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </Button>
              </div>
              <MiniMarkdown text={viewing.content} />
            </>
          )}
          {viewing && editing && (
            <>
              <DialogHeader><DialogTitle>Edit SOP</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Title</Label><Input data-testid="sop-form-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="mt-1" /></div>
                <div><Label>Category</Label><Input data-testid="sop-form-category-input" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="mt-1" /></div>
                <div><Label>Content (markdown)</Label><Textarea data-testid="sop-form-content-input" value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))} rows={14} className="mt-1 font-mono text-xs" /></div>
                <div className="flex gap-2">
                  <Button data-testid="sop-form-save-button" onClick={save} disabled={busy}>{busy && <Spinner className="mr-2" />}Save</Button>
                  <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle>New SOP</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input data-testid="sop-create-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="mt-1" placeholder="e.g. TikTok Spark Ads SOP" /></div>
            <div><Label>Category</Label><Input data-testid="sop-create-category-input" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="mt-1" /></div>
            <div><Label>Content (markdown)</Label><Textarea data-testid="sop-create-content-input" value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))} rows={12} className="mt-1 font-mono text-xs" placeholder={'## Goal\n...\n\n## Steps\n1. ...'} /></div>
            <Button data-testid="sop-create-save-button" onClick={save} disabled={busy} className="w-full">{busy && <Spinner className="mr-2" />}Create SOP</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
